import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Grid,
    Box,
    Chip,
    DialogActions,
    Button,
    Paper,
    Divider,
    IconButton,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    Info as InfoIcon,
    Link as LinkIcon,
    Settings as SettingsIcon,
    Person as PersonIcon,
    Edit as EditIcon,
    Add as AddIcon,
    Check as CheckIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import Api from "../../Services/Api";
import EditKeywordsDialog from './EditKeywords';

const InfoRow = ({ label, value, editable, onEdit, isAdmin }) => (
    <Grid container spacing={2} sx={{ mb: 1.5 }}>
        <Grid item xs={4}>
            <Typography variant="body2" color="text.secondary">
                <strong>{label}:</strong>
            </Typography>
        </Grid>
        <Grid item xs={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                    {value || 'N/A'}
                </Typography>
                {editable && isAdmin && (
                    <IconButton size="small" onClick={onEdit} color="primary">
                        <EditIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
        </Grid>
    </Grid>
);

const SectionTitle = ({ icon: Icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
        <Icon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" color="primary">
            {title}
        </Typography>
    </Box>
);

const FeedInfoDialog = ({
    open,
    onClose,
    feed,
    isAdmin,
    onKeywordsUpdated,
    keywordsList = [],
    onFeedUpdated,
}) => {
    const [isEditingKeywords, setIsEditingKeywords] = useState(false);
    const [keywordsInput, setKeywordsInput] = useState('');
    const [keywordsName, setKeywordsName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showNewKeywordsForm, setShowNewKeywordsForm] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [keywords, setKeywords] = useState([]);
    
    // Device editing state
    const [isEditingDevice, setIsEditingDevice] = useState(false);
    const [deviceValue, setDeviceValue] = useState('');
    const [isUpdatingDevice, setIsUpdatingDevice] = useState(false);

    useEffect(() => {
        if (feed) {
            console.log('Feed:', feed);
            const newKeywords = feed.demandFeed?.supply_feeds?.[0]?.supply_feed_keyword?.keywords || [];
            console.log('Keywords:', newKeywords);
            setKeywords(newKeywords);
            setDeviceValue(feed.device || 'all');
        }
    }, [feed]);

    const handleEditClick = () => {
        setEditDialogOpen(true);
    };

    const handleEditDialogClose = () => {
        setEditDialogOpen(false);
    };

    const handleCancelEdit = () => {
        setIsEditingKeywords(false);
        setKeywordsInput('');
        setKeywordsName('');
        setShowNewKeywordsForm(false);
    };

    const handleCreateAndAssignKeywords = async () => {
        setIsSubmitting(true);
        try {
            const cleanedKeywords = keywordsInput
                .split(/[\n,]/)
                .map(k => k.trim())
                .filter(k => k.length > 0);

            // Create new keyword list
            const createResponse = await Api.createKeywordList({
                name: keywordsName,
                keywords: cleanedKeywords
            });

            if (createResponse && createResponse.data) {
                // Assign the new keyword list to the feed
                await Api.updateSpinSupplyFeedKeywords(feed.supply_feed_id, {
                    keyword_list_id: createResponse.data.id
                });

                if (onKeywordsUpdated) {
                    await onKeywordsUpdated(feed.id, keywords);
                }
            }

            setIsEditingKeywords(false);
            setKeywordsInput('');
            setKeywordsName('');
            setShowNewKeywordsForm(false);
        } catch (error) {
            console.error('Error creating and assigning keywords:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeywordsUpdated = async (feedId, updatedKeywords) => {
        console.log("FeedInfoDialog.handleKeywordsUpdated", feedId, updatedKeywords);
        if (onKeywordsUpdated) {
            await onKeywordsUpdated(feedId, updatedKeywords);
        }
        setKeywords(updatedKeywords || []);
    };

    const handleDeviceEditClick = () => {
        setIsEditingDevice(true);
    };

    const handleDeviceCancel = () => {
        setIsEditingDevice(false);
        setDeviceValue(feed.device || 'all');
    };

    const handleDeviceSave = async () => {
        if (!feed || deviceValue === feed.device) {
            setIsEditingDevice(false);
            return;
        }

        setIsUpdatingDevice(true);
        try {
            const feedId = feed.demandFeed?.id || feed.demand_feed_id || feed.id;
            const response = await Api.updateSpinFeedDevice(feedId, { device: deviceValue });
            
            if (response && !response.error) {
                // Update the feed locally and notify parent
                if (onFeedUpdated) {
                    onFeedUpdated(feed.id, { device: deviceValue });
                }
                console.log('Device updated successfully');
                setIsEditingDevice(false);
            } else {
                console.error('Error updating device:', response);
                // Reset to original value on error
                setDeviceValue(feed.device || 'all');
            }
        } catch (error) {
            console.error('Error updating device:', error);
            // Reset to original value on error
            setDeviceValue(feed.device || 'all');
        } finally {
            setIsUpdatingDevice(false);
        }
    };

    const getDeviceLabel = (device) => {
        switch (device) {
            case 'all': return 'Mobile & Desktop';
            case 'mobile': return 'Only Mobile';
            case 'desktop': return 'Only Desktop';
            default: return device;
        }
    };

    if (!feed) return null;

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                pb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                <InfoIcon color="primary" />
                Feed Information
                <Chip 
                    label={feed.status} 
                    color={
                        feed.status === 'Active' ? 'success' :
                        feed.status === 'Pending' ? 'warning' :
                        feed.status === 'Inactive' ? 'error' : 
                        'default'
                    }
                    size="small"
                    sx={{ ml: 'auto' }}
                />
            </DialogTitle>
            <DialogContent>
                <Box sx={{ p: 2 }}>
                    {/* Basic Information */}
                    <SectionTitle icon={InfoIcon} title="Basic Information" />
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <InfoRow label="Feed ID" value={feed.supply_feed_id} />
                        <InfoRow label="Client ID" value={feed.identifier} />
                        <InfoRow label="Type" value={feed.type} />
                        
                        {/* Device Row with editing capability */}
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Device:</strong>
                                </Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {isEditingDevice ? (
                                        <>
                                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                                <Select
                                                    value={deviceValue}
                                                    onChange={(e) => setDeviceValue(e.target.value)}
                                                    disabled={isUpdatingDevice}
                                                >
                                                    <MenuItem value="all">Mobile & Desktop</MenuItem>
                                                    <MenuItem value="mobile">Only Mobile</MenuItem>
                                                    <MenuItem value="desktop">Only Desktop</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <IconButton
                                                size="small"
                                                onClick={handleDeviceSave}
                                                color="primary"
                                                disabled={isUpdatingDevice}
                                            >
                                                {isUpdatingDevice ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    <CheckIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={handleDeviceCancel}
                                                color="secondary"
                                                disabled={isUpdatingDevice}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="body1">
                                                {getDeviceLabel(feed.device) || 'N/A'}
                                            </Typography>
                                            {isAdmin && (
                                                <IconButton 
                                                    size="small" 
                                                    onClick={handleDeviceEditClick} 
                                                    color="primary"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        <InfoRow label="Country" value={feed.country} />
                    </Paper>

                    {/* Technical Details */}
                    <SectionTitle icon={SettingsIcon} title="Technical Details" />
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <InfoRow label="Source" value={
                            <Chip 
                                label={feed.source?.toUpperCase()} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                            />
                        } />
                        <InfoRow label="Link" value={
                            <Box sx={{ 
                                wordBreak: 'break-all', 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <Box
                                    component="code"
                                    sx={{
                                        backgroundColor: 'grey.100',
                                        padding: '4px 8px',
                                        borderRadius: 1,
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'grey.200',
                                        }
                                    }}
                                    onClick={() => window.open(feed.link, '_blank', 'noopener,noreferrer')}
                                >
                                    {feed.link}
                                </Box>
                            </Box>
                        } />
                        <InfoRow label="Cap" value={`${feed.cap?.toLocaleString()} searches/day`} />
                    </Paper>

                    {/* User Information */}
                    <SectionTitle icon={PersonIcon} title="User Information" />
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <InfoRow label="User" value={feed.demand_user?.name} />
                        <InfoRow label="Notes" value={feed.notes} />
                    </Paper>

                    {/* Timestamps */}
                    <SectionTitle icon={ScheduleIcon} title="Timestamps" />
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <InfoRow 
                            label="Created" 
                            value={new Date(feed.created_at).toLocaleString()} 
                        />
                        <InfoRow 
                            label="Last Updated" 
                            value={new Date(feed.updated_at).toLocaleString()} 
                        />
                    </Paper>

                    {/* Modified Keywords Section */}
                    <SectionTitle icon={InfoIcon} title="Keywords List" />
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Box>
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                mb: 2
                            }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Current Keywords:
                                </Typography>
                                {isAdmin && (
                                    <IconButton 
                                        size="small" 
                                        onClick={handleEditClick}
                                        color="primary"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {keywords.length > 0 ? (
                                    keywords.map((keyword, index) => (
                                        <Chip
                                            key={index}
                                            label={keyword}
                                            size="small"
                                            variant="outlined"
                                            sx={{ 
                                                borderRadius: 1,
                                                backgroundColor: 'action.hover'
                                            }}
                                        />
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No keywords available
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </DialogContent>
            <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
                <Button 
                    onClick={onClose} 
                    variant="contained"
                    sx={{ minWidth: 100 }}
                >
                    Close
                </Button>
            </DialogActions>
            <EditKeywordsDialog
                open={editDialogOpen}
                onClose={handleEditDialogClose}
                feed={feed}
                isAdmin={isAdmin}
                onKeywordsUpdated={handleKeywordsUpdated}
                keywordLists={keywordsList}
                setSnackbarMessage={(msg) => console.log("Snackbar Message:", msg)}
                setSnackbarSeverity={(sev) => console.log("Snackbar Severity:", sev)}
                setSnackbarOpen={(open) => console.log("Snackbar Open:", open)}
            />
        </Dialog>
    );
};

export default FeedInfoDialog; 